"use client";
import { IBaseLabelValue } from "@/interfaces";
import { CustomButton, FlexBetween, FlexCenter, FlexCol } from "@/components";
import { cn, convertToJalaliDateTime } from "@/lib";
import { useTranslations } from "next-intl";
import { useRouter } from "next/navigation";
import InputDataContent from "@/components/inquiryDetails/inputDataContent";
import { routes } from "@/routes";
import { useRef } from "react";
import ResultContent from "@/components/resultContent";
import PdfTemplate from "@/components/pdfTemplates/pdfTemplate";
import DownloadPdf from "@/components/pdfTemplates/downloadPdf";
import {
  IIdentificationDocuments,
  IRawPurchasedForeignersInquiryResData,
} from "@/interfaces/inquiries/governmental";

const ForeignersInquiryDetails = ({
  data,
}: {
  data: IRawPurchasedForeignersInquiryResData;
}) => {
  const t = useTranslations("serviceResponseDetails");
  const router = useRouter();
  const foreignersData = data.data.data;

  const input = data.data.input;
  const inputContent: IBaseLabelValue[] = [
    {
      label: t("foreignersInquiry.fida"),
      value: input.fida,
    },
    {
      label: t("foreignersInquiry.birthDate"),
      value: input.birth_date || input.birthDate || "-",
    },
  ];

  const generateGender = (gender: string): string => {
    switch (gender) {
      case "MALE":
        return "مرد";
      case "FEMALE":
        return "زن";
      case "UNKNOWN":
        return "نامشخص";
      default:
        return gender || "";
    }
  };

  const pdfContentRef = useRef<HTMLDivElement>(null);
  const birthdatePersian = foreignersData.birthDate
    ? convertToJalaliDateTime(foreignersData.birthDate)?.split("-")
    : undefined;
  const birthdate = foreignersData.birthDate?.split("T") ?? [];
  const resultContent: IBaseLabelValue[] = [
    {
      label: t("foreignersInquiry.firstName"),
      value: foreignersData.firstName || "-",
    },
    {
      label: t("foreignersInquiry.lastName"),
      value: foreignersData.lastName || "-",
    },
    {
      label: "نام کامل",
      value: foreignersData.fullName || "-",
    },
    {
      label: t("foreignersInquiry.fatherName"),
      value: foreignersData.fatherName || "-",
    },
    {
      label: t("foreignersInquiry.birthDatePersian"),
      value: birthdatePersian?.[1] ? birthdatePersian[1] : "-",
    },
    {
      label: t("foreignersInquiry.birthDate"),
      value: birthdate[0] ? birthdate[0].replace(/-/g, "/") : "-",
    },
    {
      label: t("foreignersInquiry.ancestorName"),
      value: foreignersData.ancestorName || "-",
    },
    {
      label: t("foreignersInquiry.gender"),
      value: generateGender(foreignersData.gender) || "-",
    },
    {
      label: t("foreignersInquiry.birthPlaceCountry"),
      value: foreignersData.birthPlaceCountry || "-",
    },
    {
      label: t("foreignersInquiry.birthPlaceCity"),
      value: foreignersData.birthPlaceCity || "-",
    },
    {
      label: t("foreignersInquiry.nationality"),
      value: foreignersData.nationality || "-",
    },
    {
      label: t("foreignersInquiry.provinceName"),
      value: foreignersData.provinceName || "-",
    },
    {
      label: "نوع مدرک شناسایی",
      value: foreignersData.identificationDocumentTypeTitle || "-",
    },
    {
      label: "شماره مدرک شناسایی",
      value: foreignersData.identificationDocumentNumber || "-",
    },
    {
      label: t("foreignersInquiry.status"),
      value:
        typeof foreignersData.status === "number"
          ? foreignersData.status === 1
            ? "فعال"
            : "غیرفعال"
          : "-",
    },
    {
      label: t("foreignersInquiry.oldFidaCode"),
      value: foreignersData.oldFidaCode || "-",
    },
    {
      label: t("foreignersInquiry.uniqueCode"),
      value: foreignersData.uniqueCode || "-",
    },
    {
      label: t("foreignersInquiry.familyId"),
      value: foreignersData.familyId || "-",
    },
    {
      label: t("foreignersInquiry.exited"),
      value:
        typeof foreignersData.exited === "boolean"
          ? foreignersData.exited
            ? t("foreignersInquiry.isExist")
            : t("foreignersInquiry.isNotExit")
          : "-",
    },
    {
      label: t("foreignersInquiry.detectionCode"),
      value: foreignersData.detectionCode || "-",
    },
    {
      label: t("foreignersInquiry.relative"),
      value:
        typeof foreignersData.relative === "number" &&
        foreignersData.relative > 0
          ? `${foreignersData.relative} نفر `
          : foreignersData.relative !== undefined
            ? String(foreignersData.relative)
            : "-",
    },
    {
      label: t("foreignersInquiry.education"),
      value:
        typeof foreignersData.education === "number"
          ? foreignersData.education === 1
            ? t("foreignersInquiry.exist")
            : t("foreignersInquiry.notExist")
          : "-",
    },
  ];

  function createDocFields(): IBaseLabelValue[] | undefined {
    if (
      !Array.isArray(foreignersData.identificationDocuments) ||
      foreignersData.identificationDocuments.length === 0
    ) {
      return;
    }

    return foreignersData.identificationDocuments
      ?.map((item) =>
        (Object.keys(item) as (keyof IIdentificationDocuments)[]).map((key) => {
          let value = item[key];

          if (key === "issueDate" || key === "expirationDate") {
            value = convertToJalaliDateTime(String(value));
          }

          return {
            label: t(`foreignersInquiry.${String(key)}` as any),
            value: String(value ?? ""),
          };
        }),
      )
      .flat();
  }
  const documentsData = createDocFields();
  return (
    <div className="shadow-main from-teal-ocean relative basis-[500px] rounded-2xl bg-linear-to-b to-transparent p-[2px] lg:basis-[640px]">
      <FlexCol className="items-stretch rounded-2xl bg-white p-8">
        <h2 className="mb-5 text-center font-bold lg:text-lg">
          {t("foreignersInquiry.title")}
        </h2>
        <InputDataContent
          content={inputContent}
          className="le p-6 md:p-8"
          itemClassName="flex-col md:flex-row leading-9 md:leading-none"
        />
        <FlexBetween className="mb-3 items-center">
          <h2 className="text-center text-lg font-bold">
            {t("accountToIban.resultTitle")}
          </h2>
          <DownloadPdf
            id="foreigners-inquiry-download-pdf"
            htmlContentRef={pdfContentRef}
            useHtmlToPdf={true}
            fileName="اطلاعات هویتی اتباع خارجی"
          />
        </FlexBetween>
        <ResultContent content={resultContent} />
        {documentsData && (
          <>
            <p className="mt-3 text-base font-semibold">اسناد هویتی</p>
            <ResultContent content={documentsData} />
          </>
        )}

        <FlexCenter className="flex flex-col justify-center md:flex-row md:gap-6">
          <DownloadPdf
            id="foreigners-inquiry-download-pdf"
            fileName="اطلاعات هویتی اتباع خارجی"
            htmlContentRef={pdfContentRef}
            useHtmlToPdf={true}
            className="order-2 flex cursor-pointer items-center justify-center rounded-md border-none! bg-red-700 px-8 py-[6px] text-white transition-all hover:bg-red-800 md:order-1 lg:rounded-lg lg:py-[8px]"
            showIcon={false}
            labelClass="text-xs lg:text-sm xl:text-base font-medium"
          />
          <div className="order-1 mb-4 flex gap-4 md:order-2 md:m-0 xl:gap-6">
            <CustomButton
              id="details-foreigners-inquiry-back"
              className={cn("w-fit bg-gray-500")}
              onClick={() =>
                router.push(routes.main.INQUIRY_HISTORY.primaryPath)
              }
            >
              {t("companyInformationInquiry.backButton")}
            </CustomButton>
            <CustomButton
              id="details-foreigners-inquiry-new-request"
              onClick={() =>
                router.push(routes.services.Foreigners_Inquiry.rewritePath)
              }
              className={cn("bg-primary-main mx-auto w-fit px-8! text-white")}
            >
              {t("newRequest")}
            </CustomButton>
          </div>
        </FlexCenter>
      </FlexCol>
      <div
        ref={pdfContentRef}
        style={{
          position: "fixed",
          left: "-9999px",
          top: "-9999px",
          width: "800px",
          fontFamily: "IRANYekan, sans-serif",
          margin: "2rem auto",
        }}
      >
        <PdfTemplate
          tableTitle="نتیجه استعلام اطلاعات اتباع خارجی"
          resultContent={resultContent}
          code={data.code}
          dateTime={data.created_at}
        />
        {documentsData && (
          <>
            <p className="mt-3 text-base font-semibold">اسناد هویتی</p>
            <ResultContent forPdf content={documentsData} />
          </>
        )}
      </div>
    </div>
  );
};

export default ForeignersInquiryDetails;
